import React, { useState, useEffect } from "react";
import { Button, Dropdown, Menu, Input, Tag, Spin, Typography, Divider } from "antd";
import { 
  FileTextOutlined, 
  SearchOutlined, 
  SendOutlined,
  SettingOutlined,
  FileImageOutlined
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getActiveTemplatesRequest,
  getTemplatesByCategoryRequest,
  searchTemplatesRequest,
  messageTemplateSelectors,
} from "providers/MessageTemplateProvider/slice";
import "./styles.less";

const { Search } = Input;
const { Text } = Typography;

const TemplateSelector = ({ onSelectTemplate, onUseTemplate }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);

  const loading = useSelector((state) => state.messageTemplate.loading);
  const searchLoading = useSelector((state) => state.messageTemplate.searchLoading);
  const activeTemplates = useSelector((state) => state.messageTemplate.activeTemplates);
  const searchResults = useSelector((state) => state.messageTemplate.searchResults);
  const categoryTemplates = useSelector((state) => state.messageTemplate.categoryTemplates);

  useEffect(() => {
    if (visible && !searchValue && !selectedCategory) {
      dispatch(getActiveTemplatesRequest());
    }
  }, [dispatch, visible, searchValue, selectedCategory]);

  const handleSearch = (value) => {
    setSearchValue(value);
    setSelectedCategory(null);
    if (value.trim()) {
      dispatch(searchTemplatesRequest({ query: value }));
    } else {
      dispatch(getActiveTemplatesRequest());
    }
  };

  const handleCategoryFilter = (category) => {
    console.log('Category filter clicked:', category);
    setSelectedCategory(category);
    setSearchValue("");
    dispatch(getTemplatesByCategoryRequest({ category }));
  };

  const handleTemplateSelect = (template) => {
    setVisible(false);
    onSelectTemplate(template);
  };

  const handleUseTemplate = (template, e) => {
    e.stopPropagation();
    setVisible(false);
    onUseTemplate(template);
  };

  const handleManageTemplates = () => {
    setVisible(false);
    navigate('/fonehouse/message-templates');
  };

  const renderPreviewContent = (content) => {
    if (!content) return "";
    
    // Replace placeholders with example values for preview
    const placeholders = {
      "{customer_name}": "Khách hàng",
      "{product_name}": "Sản phẩm",
      "{order_id}": "Đơn hàng",
      "{price}": "Giá",
      "{date}": "Ngày",
    };

    let preview = content;
    Object.entries(placeholders).forEach(([placeholder, value]) => {
      preview = preview.replace(new RegExp(placeholder.replace(/[{}]/g, "\\$&"), "g"), value);
    });

    return preview;
  };

  const getDisplayTemplates = () => {
    if (searchValue) {
      return searchResults; // Show search results even if empty
    }
    if (selectedCategory) {
      return categoryTemplates; // Show category results even if empty  
    }
    return activeTemplates;
  };

  const templates = getDisplayTemplates();
  
  // Debug logging
  useEffect(() => {
    console.log('Template Selector Debug:', {
      searchValue,
      selectedCategory,
      activeTemplates,
      searchResults,
      categoryTemplates,
      templates,
      loading,
      searchLoading
    });
  }, [searchValue, selectedCategory, activeTemplates, searchResults, categoryTemplates, templates, loading, searchLoading]);

  const menu = (
    <div className="template-selector-dropdown">
      <div className="template-selector-header">
        <Search
          placeholder="Tìm kiếm template..."
          allowClear
          onSearch={handleSearch}
          style={{ marginBottom: 8 }}
        />
        
        <div className="category-filters">
          <Button
            size="small"
            type={!selectedCategory ? "primary" : "default"}
            onClick={() => {
              setSelectedCategory(null);
              setSearchValue("");
              dispatch(getActiveTemplatesRequest());
            }}
          >
            Tất cả
          </Button>
          <Button
            size="small"
            type={selectedCategory === "greeting" ? "primary" : "default"}
            onClick={() => handleCategoryFilter("greeting")}
          >
            Chào hỏi
          </Button>
          <Button
            size="small"
            type={selectedCategory === "order" ? "primary" : "default"}
            onClick={() => handleCategoryFilter("order")}
          >
            Đơn hàng
          </Button>
          <Button
            size="small"
            type={selectedCategory === "support" ? "primary" : "default"}
            onClick={() => handleCategoryFilter("support")}
          >
            Hỗ trợ
          </Button>
          <Button
            size="small"
            type={selectedCategory === "promotion" ? "primary" : "default"}
            onClick={() => handleCategoryFilter("promotion")}
          >
            Khuyến mãi
          </Button>
        </div>
      </div>

      <div className="template-list">
        {(loading || searchLoading) ? (
          <div className="template-loading">
            <Spin size="small" />
            <Text type="secondary">Đang tải template...</Text>
          </div>
        ) : templates.length > 0 ? (
          templates.map((template) => (
            <div
              key={template._id}
              className="template-item"
              onClick={() => handleTemplateSelect(template)}
            >
              <div className="template-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1 }}>
                  <Text strong className="template-title" style={{ fontSize: '13px' }}>
                    {template.title}
                  </Text>
                  {template.image && (
                    <FileImageOutlined 
                      style={{ color: '#1890ff', fontSize: 11 }} 
                      title="Template có hình ảnh"
                    />
                  )}
                </div>
                <div className="template-actions">
                  <Button
                    type="link"
                    size="small"
                    icon={<SendOutlined />}
                    onClick={(e) => handleUseTemplate(template, e)}
                    title="Sử dụng ngay"
                    style={{ padding: '2px 4px', height: 'auto' }}
                  />
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                {template.category && (
                  <Tag size="small" color="blue" style={{ margin: 0, fontSize: '11px' }}>
                    {template.category}
                  </Tag>
                )}
              </div>
              
              <div className="template-content">
                {template.image && (
                  <div className="template-image" style={{ marginBottom: 6 }}>
                    <img
                      src={typeof template.image === 'string' ? template.image : template.image.url}
                      alt="Template"
                      style={{
                        width: '100%',
                        maxHeight: 60,
                        objectFit: 'cover',
                        borderRadius: 4,
                        border: '1px solid #d9d9d9'
                      }}
                    />
                  </div>
                )}
                <Text 
                  type="secondary" 
                  className="template-preview"
                  style={{ 
                    fontSize: '12px',
                    lineHeight: '1.3',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {renderPreviewContent(template.content)}
                </Text>
              </div>
            </div>
          ))
        ) : (
          <div className="template-empty">
            <Text type="secondary">
              {searchValue 
                ? "Không tìm thấy template nào" 
                : "Chưa có template nào"}
            </Text>
          </div>
        )}
      </div>

      <Divider style={{ margin: "8px 0" }} />
      
      <div className="template-footer">
        <Button
          type="link"
          icon={<SettingOutlined />}
          onClick={handleManageTemplates}
          style={{ width: "100%", textAlign: "left" }}
        >
          Quản lý Template
        </Button>
      </div>
    </div>
  );

  return (
    <Dropdown
      overlay={menu}
      trigger={["click"]}
      visible={visible}
      onVisibleChange={setVisible}
      placement="topLeft"
      overlayClassName="template-selector-overlay"
    >
      <Button
        icon={<FileTextOutlined />}
        title="Chọn template tin nhắn"
        className="template-selector-button"
        size="small"
        type="text"
        style={{ 
          width: 32, 
          height: 32, 
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      />
    </Dropdown>
  );
};

export default TemplateSelector;